// The wire protocol between the game client and the match room worker (party/index.ts imports these
// same types, so client and server can never drift apart). Plain JSON messages over one WebSocket.
//
// Design (docs/MULTIPLAYER.md): the room is a RELAY + tiny authority - it orders commands with a
// monotonic `seq`, keeps the lobby roster, stores the latest snapshot for reconnects, and enforces the
// version handshake. It runs NO game logic: every client re-runs the deterministic sim locally.

import type { Command } from '../game/command';
import type { Difficulty } from '../game/types';

// v2: the room broadcasts an authoritative per-turn clock ({t:'clock'}), so every client draws the
// SAME countdown from the same absolute deadline instead of arming a local timer after its own replay.
export const PROTOCOL_VERSION = 2;
// Engine build tag. Lockstep is only bit-identical between IDENTICAL engine builds (float thresholds,
// stable-sort tie-breaks), so clients with different tags must never share a match. Bump this whenever
// anything under src/game changes simulation results.
// v3: decisive-capture game end runs inside applyCommand (shared command application, not just the local
// pointerup), makePeace on a human routes through the consent-offer flow, and reclaimSeat/aiTakeover's
// last-human finish exist - all of which change WHEN state flips, so mixed builds would desync.
// v4: allyBroken inbox cards survive runTurnGen's end-of-walk message rebuild, the round-cap finish
// fires at the wrap to round LIMIT+1 (no post-wrap AI turns the humans never got), and respondOffer
// is applied in relay order on EVERY client including the sender (no optimistic local apply).
// v5: ROUND_LIMIT 40 -> 60 (total-conquest matches need the longer runway); a mixed table would end
// the game at different rounds, so old and new builds must never share a match.
// v6: the land-mode expansion. Sea crossings became two-step voyages (board a boat, land next turn:
// GameState.voyages), mapKind 'land' generates one connected mainland with Barracks as the supply
// building, and gate terrain grants the Wall damage reduction - all of which change sim results.
// v7: voyage resolution moved to the turn-walk seams (seat-control-flip safe), AI peace asks at
// humans rate-limited (peaceAskStamp), the land AI skips Seafaring, and land maps grew roads +
// mountain terrain - sim-visible, so v6 and v7 builds must never share a match.
// v8: garrison factions (scenario castle lords hold their walls; Faction.garrison gates AI movement).
// Never set in multiplayer, but it lives under src/game, so the tag moves with it.
// v9: a boat whose beach was taken in peacetime now rides at anchor for up to two turns before
// sailing home (Voyage.waited), which is state every client must agree on.
// v10: one rule set for both maps (Seafaring AND Siegecraft everywhere, Harbor AND Barracks buildable
// everywhere), crossings launch only from a PORT TILE, mainland coasts sail within SEA_RANGE, and a
// blocked boat stalls for its owner to re-aim (reroute command) instead of auto-diverting.
// v11: no-parley factions (scenario raider bands refuse peace, alliances and trade), campaign
// levels carry their own victory goal (GameState.objective), and Harbors pay gold instead of army cap.
// v12: isCoastal is a REAL coastline test (touches open water) instead of a neighbour count, so
// mountains and castle walls no longer fake a shoreline: harbours, landings and AI ports all move.
// v13: troops are raised only at a muster ground (Barracks or the capital), a Barracks shields its
// defender by 1, and a fallen realm's boats sink with it.
// v14: an archer's range is no longer gated by island identity (on a mainland that meant PROVINCE, so
// archers could never shoot over castle walls), the AI threat model sees across provinces, and the AI
// re-aims its own stalled boats instead of leaving them frozen at sea.
// v20: worldgen variety (curated free-play sizes 8/10/12, symmetric island cliffs, the neutral heart
// isle). Worlds are host-generated and snapshot-shipped, so no desync path exists, but a pre-variety
// build must not share a table with this one: its lobby still writes the retired 7/9/11 sizes.
// v21: SIM rules changed: domination now counts only conquerable tiles (cliffs/walls out of the
// denominator) and the free-play CROWN RULE ties the army cap to capitals held. Both run inside the
// lockstep sim, so mixed builds would disagree on caps and on when a match ends.
// v22: the MP last-stand shield now protects HUMAN seats only (AI fill fought as unkillable early);
// replay events also carry `retaliated`. Both change sim-produced state, so builds must not mix.
export const ENGINE_VERSION = 'wi-mp-22';

export interface MatchSettings {
  factionCount: number; // total seats (3-5); humans fill from seat 0, the rest are AI
  islandSize: number; // free-play sizes: 8 | 10 | 12 (older builds sent 7/9/11; any number generates)
  dominationPct: number; // always 1 (total conquest) since the win-condition option was removed; old builds may still send 0.6
  difficulty: Difficulty; // AI difficulty for the fill seats
  turnTimerSec: 30 | 45 | 60 | 90 | 120; // per-move timer. The lobby offers 60/90/120 (players asked for room to think); 30/45 stay legal for legacy rooms and the fast headless net tests
  mapKind?: 'isles' | 'land'; // optional: rooms stored by pre-land-mode workers lack it; read as 'isles'
}

// 60s default move timer: newcomers who don't know the game yet kept running out of clock on shorter
// defaults; a host who wants a brisker table can still pick 30/45 in the lobby.
export const DEFAULT_SETTINGS: MatchSettings = { factionCount: 4, islandSize: 12, dominationPct: 1, difficulty: 'normal', turnTimerSec: 120, mapKind: 'isles' };

export interface LobbyMember {
  id: string; // stable player id (the token from hello; survives refresh, so reconnects keep the seat)
  name: string;
  ready: boolean;
  host: boolean;
  avatar?: string; // the player's equipped cosmetic avatar id (shown on their lobby seat card)
  frame?: string; // the player's equipped cosmetic frame id
  race?: string | null; // lobby race pick (null/undefined = dealt randomly at start)
}

export interface LobbyState {
  members: LobbyMember[];
  settings: MatchSettings;
  started: boolean;
}

// --- client → server ---
export type ClientMsg =
  // `token` is a per-tab stable identity (survives page refresh), so the room can recognise a player
  // reconnecting to a started match and hand them back their seat. `create` marks a freshly-minted room
  // code: the room refuses it if it is already occupied (code collision), so the client can roll again.
  | { t: 'hello'; protocol: number; engine: string; name: string; token: string; avatar?: string; frame?: string; create?: boolean }
  | { t: 'ready'; ready: boolean }
  | { t: 'cosmetics'; avatar?: string; frame?: string } // equipped look changed mid-lobby; the room updates the seat card
  | { t: 'pickRace'; race: string | null } // lobby race choice (null = random); uniqueness enforced by the room
  | { t: 'settings'; settings: Partial<MatchSettings> } // host only
  | { t: 'kick'; playerId: string } // host only
  // host only: the fully-built starting state + which member sits in which faction seat
  | { t: 'start'; snapshot: string; seats: Record<string, number>; hash: number }
  | { t: 'cmd'; cmd: Command } // one player action; the room stamps seq + fans out
  | { t: 'hash'; turnSeq: number; hash: number; turnOf: number } // per-turn desync check + tells the room whose turn began (drives its AFK timer)
  // any client MAY push a fresh reconnect snapshot after its per-turn hash; the room ACCEPTS it only
  // from its computed provider (the host when connected, else the lowest-seated connected member)
  | { t: 'snapshotPush'; snapshot: string; turnSeq: number }
  | { t: 'snapshotRequest' }
  // the sender's local sim reached game over. When every CONNECTED seated member has said so, the room
  // tears itself down: rejoining a finished match must not replay the result (double-paid rewards), and
  // a completed match must not squat in Durable Object storage forever.
  | { t: 'finished' }
  // a CONNECTED owner of an AI-taken seat asks for it back (backgrounded tab that never dropped its
  // socket: the reconnect-triggered reclaim never fires, so without this the seat is stuck AI-run)
  | { t: 'reclaimSeat' }
  // "play again with this group": after game over any seated member can ask the room to reset back to a
  // fresh lobby (same members), so the group continues without leaving the game (CrazyGames round-continuation)
  | { t: 'rematch' };

// WebSocket close codes the room uses (client maps them to human-readable statuses):
// 4002 match/room closed · 4003 too many bad messages · 4004 kicked by the host · 4005 replaced by a
// newer connection with the same token (duplicate tab; the closed side must NOT auto-reconnect).
export const CLOSE_KICKED = 4004;
export const CLOSE_REPLACED = 4005;

// --- server → client ---
export type ServerMsg =
  | { t: 'welcome'; you: string; lobby: LobbyState }
  | { t: 'error'; code: 'version' | 'full' | 'started' | 'notHost' | 'notReady' | 'exists' | 'abandoned' | 'badMsg'; msg: string }
  | { t: 'lobby'; lobby: LobbyState }
  // `match` is a server-minted nonce identifying THIS match: room codes are reusable (a finished room's
  // code can host a fresh rematch), so anything keyed per-match (the client's once-per-match reward
  // guard) must key on code+match, never the code alone.
  | { t: 'start'; snapshot: string; seats: Record<string, number>; hash: number; seq: number; match?: string }
  | { t: 'cmd'; from: string; seat: number; cmd: Command; seq: number }
  // AFK enforcement, ordered through the same seq stream as commands so every client applies them at
  // the same point: forceEnd = the room timed the acting seat out (apply an endTurn on its behalf);
  // takeover = repeated timeouts/disconnect, the seat is handed to the AI from the next round on.
  // the AUTHORITATIVE turn clock: broadcast once when a turn's first 'hash' report arms the room's AFK
  // timer (and re-sent to rejoiners). `deadlineAt`/`now` are the ROOM's epoch ms; a client renders
  // left = deadlineAt - (Date.now() + (now - receiveTime)), so wall-clock skew between machines cancels.
  | { t: 'clock'; seat: number; deadlineAt: number; now: number }
  | { t: 'forceEnd'; seat: number; seq: number }
  | { t: 'takeover'; seat: number; seq: number }
  // a taken-over seat's owner returned: the seat becomes human again from now on, ordered through the
  // same seq stream so the rejoiner (whose catch-up buffer already holds the takeover) and every peer
  // flip it back at the same point
  | { t: 'reclaim'; seat: number; seq: number }
  // the room was reset to a fresh lobby for a rematch: every client returns to the lobby (reconnecting to
  // the same room code lands them back together)
  | { t: 'rematch' }
  | { t: 'hashMismatch'; turnSeq: number; hashes: Record<string, number> }
  | { t: 'peer'; playerId: string; connected: boolean }
  // the desync-heal reply: the stored snapshot plus every ordered event relayed since it was taken
  // (same shape of catch-up as 'rejoin'), so a resyncing client lands on the LIVE state, not on a
  // stale turn boundary that would wedge it one turn behind the table
  | { t: 'snapshot'; snapshot: string; turnSeq: number; cmds?: ServerMsg[] }
  // reconnect into a STARTED match: the latest snapshot plus every ordered event relayed since it was
  // taken, so the rejoining client replays them and lands exactly on the live state. `names` maps token
  // to display name for ALL members (connected or not), so every seat can be labelled after a rejoin.
  | { t: 'rejoin'; snapshot: string; turnSeq: number; seats: Record<string, number>; cmds: ServerMsg[]; names: Record<string, string>; match?: string };

/** The relay host to talk to: local dev server by default, overridable for the deployed worker
 *  (e.g. VITE_PARTY_HOST=warring-isles-mp.<account>.workers.dev). */
export const PARTY_HOST: string = (import.meta as unknown as { env?: Record<string, string> }).env?.['VITE_PARTY_HOST'] ?? 'localhost:1999';

/** ws(s) URL of a match room on the party server. */
export function roomUrl(roomCode: string): string {
  const secure = PARTY_HOST.includes('partykit.dev') || PARTY_HOST.includes('workers.dev') || PARTY_HOST.startsWith('https');
  const host = PARTY_HOST.replace(/^https?:\/\//, '');
  return `${secure ? 'wss' : 'ws'}://${host}/parties/main/${encodeURIComponent(roomCode.toLowerCase())}`;
}
